import {
  AddressLookup,
  AgreementNo,
  AuthFill,
  Button,
  Comments,
  CompanyName,
  ContactGrid,
  DateOfBirth,
  Dealers,
  DropDown,
  Email,
  FinancialTerms,
  FirstName,
  LastName,
  MarketingTerms,
  MembershipNo,
  Tel,
  Title,
  VehicleRegNo,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useQuery } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ERROR_MESSAGE,
  TYPE_OF_ENQUIRIES_FINANCE,
  TYPE_OF_ENQUIRIES_GENERAL,
  TYPE_OF_ENQUIRIES_MULTIMEDIA,
  TYPE_OF_ENQUIRIES_ROADSIDE,
} from '@utils/constants'
import { closeOverlay, getPageData } from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { ContactUsSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from 'upper-case-first'

const ContactUs = ({ content, tracking, brand }) => {
  const query = useQuery()
  const [submitResponse, setSubmitResponse] = useState('')
  const [user, setUser] = useState(false)
  const [formStep, setFormStep] = useState(1)
  const [showDealer, setShowDealer] = useState(false)

  const typeOfEnquiry = {
    'general-enquiries': TYPE_OF_ENQUIRIES_GENERAL,
    'roadside-assistance': TYPE_OF_ENQUIRIES_ROADSIDE,
    multimedia: TYPE_OF_ENQUIRIES_MULTIMEDIA,
    'financial-services': TYPE_OF_ENQUIRIES_FINANCE,
  }

  const step = (newFormStep, userState) => {
    setUser(userState)
    setFormStep(newFormStep)
  }

  const initialValues = {
    form_name: '',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    day: '',
    month: '',
    year: '',
    agreement_or_reg_num: '',
    type_of_enquiry: '',
    house_number: '',
    address_line1: '',
    address_line2: '',
    town: '',
    county: '',
    postcode: '',
    telephone_number: '',
    mobile_number: '',
    additional_info: '',
    company_name: '',
    enquiry_description: '',
    urn: '',
    dealer: '',
    dealer_name: '',
    dealer_id: '',
    membership_number: '',
    reg_num: '',
    fleet_type: '',
    is_lexus: brand === 'lexus',
    toyota_owner: false,
  }

  const methods = useForm({
    resolver: yupResolver(ContactUsSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (query?.type) {
      const formName = methods.getValues('form_name')
      if (formName && typeOfEnquiry[formName]) {
        const result = Object.values(typeOfEnquiry[formName]).find(
          (item) => item?.label?.toLowerCase() === query?.type?.toLowerCase(),
        )
        methods.setValue('enquiry_description', result?.label)
        methods.setValue('type_of_enquiry', result?.value)
      }
    }
  }, [methods, query, typeOfEnquiry])

  const CardStep = () =>
    methods.getValues('form_name') ? (
      <div className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}>
        <div className="md:flex flex-col">
          <button
            className="flex hover:underline"
            onClick={() => {
              step(1, true)
              methods.reset(initialValues)
              setSubmitResponse('')
              setShowDealer(false)
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="contact-us"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="“select-enquiry"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                1. Type
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
          <div className="md:flex text-tiny hidden mt-1">
            {upperCaseFirst(
              content.forms[methods.getValues('form_name')].name,
            ).replace(/-/g, ' ')}
          </div>
        </div>
      </div>
    ) : (
      <div className="flex flex-row">
        <div className={`${formStep === 2 ? 'font-medium' : 'font-semibold'}`}>
          1. Type
        </div>
      </div>
    )

  const DetailsStep = () => (
    <div className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}>
      <div className="md:flex items-center"></div>
      <div className="flex flex-row">
        <div className={`${formStep === 1 ? 'font-medium' : 'font-semibold'}`}>
          2. Details
        </div>
      </div>
    </div>
  )

  useEffect(() => {
    if (query?.form || query?.method) {
      const isActive = content.forms[query.form || query.method].active
      if (isActive) {
        methods.setValue('form_name', query.form || query.method)
      }

      if (query?.type) {
        const isTypeAvailable = typeOfEnquiry[query.form || query.method].find(
          (o) => o.value === query.type,
        )
        if (isTypeAvailable) {
          methods.setValue('type_of_enquiry', isTypeAvailable.value)
          methods.setValue('enquiry_description', isTypeAvailable.label)
          setShowDealer(isTypeAvailable.value === 'general_enquiry:2')

          if (isTypeAvailable.value === 'general_enquiry:2') {
            methods.resetField('dealer')
          }
        }
      }
    }
  }, [query])

  useEffect(() => {
    const type = methods.getValues('form_name')
    if (type) {
      step(2, user)

      if (brand === 'lexus' && type === 'general-enquiries') {
        methods.setValue('type_of_enquiry', 'general')
        methods.setValue('enquiry_description', 'Help with My Lexus')
      }

      if (brand === 'lexus' && type === 'multimedia') {
        methods.setValue('type_of_enquiry', 'mm_nav')
        methods.setValue('enquiry_description', 'Help with My Lexus (Nav)')
      }
    }
  }, [methods.watch('form_name')])

  useEffect(() => {
    const dealer = methods.getValues('dealer')
    if (dealer) {
      methods.setValue('dealer_id', dealer.split('::')[1]?.trim())
      methods.setValue('dealer_name', dealer.split('::')[0]?.trim())
    }
  }, [methods.watch('dealer')])

  const onSubmit = async (data) => {
    setSubmitResponse('')
    const parsedData = { ...data }
    switch (parsedData.form_name) {
      case 'financial-services':
        parsedData.form_name = 'tfs_contact_form'
        break
      case 'fleet-services':
        parsedData.form_name = 'fleet_contact_form'
        parsedData.type_of_enquiry = 'Other'
        break
      default:
        parsedData.form_name = 'contact_form'
        break
    }
    if (query?.path) {
      parsedData.additional_info = `${parsedData?.additional_info} | User entered via the Multimedia tool path: ${query?.path}`
    }

    if (parsedData.day) {
      parsedData.date_of_birth = `${data.day}/${data.month}/${data.year}`
    }

    // eslint-disable-next-line prefer-destructuring
    parsedData.type_of_enquiry = parsedData.type_of_enquiry.split(':')[0]

    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/contact-us-submissions`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: parsedData,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: data.form_name,
          page: tracking.page,
        })
        closeOverlay(
          content.forms[data.form_name].confirmation.title,
          content.forms[data.form_name].confirmation.copy,
          setSubmitResponse,
          false,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: data.form_name,
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        steps={[CardStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormProvider {...methods}>
          <FormStep
            subtitle={content.subtitle}
            step={formStep}
            active={formStep === 1}
            user={user}
            tracking={{
              conversion: 0,
              name: tracking.form,
              step: 'select-type',
              variant: tracking.variant,
              page: tracking.page,
            }}
          >
            <ContactGrid forms={content.forms} setUser={setUser} />
          </FormStep>

          <FormStep
            title={content?.forms[methods.getValues('form_name')]?.name}
            subtitle={
              content?.forms[methods.getValues('form_name')]?.details.subtitle
            }
            step={formStep}
            active={formStep === 2}
            user={user}
            tracking={{
              conversion: 0,
              name: tracking.form,
              step: 'contact-details',
              variant: methods.getValues('form_name') || '',
              page: tracking.page,
            }}
          >
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              {/* GENERAL FORM */}
              {methods.getValues('form_name') === 'general-enquiries' && (
                <>
                  <AuthFill />
                  {brand === 'toyota' && (
                    <DropDown
                      name="type_of_enquiry"
                      label="Type of Enquiry"
                      options={TYPE_OF_ENQUIRIES_GENERAL}
                      onChange={(e) => {
                        methods.setValue(
                          'enquiry_description',
                          TYPE_OF_ENQUIRIES_GENERAL[e.target.selectedIndex - 1]
                            .label,
                        )
                        methods.setValue(
                          'type_of_enquiry',
                          TYPE_OF_ENQUIRIES_GENERAL[e.target.selectedIndex - 1]
                            .value,
                        )

                        setShowDealer(e.target.value === 'general_enquiry:2')

                        if (e.target.value === 'general_enquiry:2') {
                          methods.resetField('dealer')
                        }
                      }}
                    />
                  )}
                  <Title />
                  <FirstName />
                  <LastName />
                  <Email />
                  {brand === 'lexus' && <Tel optional />}
                  {showDealer && <Dealers label="Dealer" optional />}
                  <VehicleRegNo optional />
                  <Comments
                    optional
                    className="resize-y w-full"
                    name="additional_info"
                    label="Your Question"
                    characterLimit={2000}
                  />
                  <MarketingTerms />
                  <Button submitResponse={submitResponse}>
                    {content.forms['general-enquiries'].details.submit}
                  </Button>
                </>
              )}
              {/* ROADSIDE FORM */}
              {methods.getValues('form_name') === 'roadside-assistance' && (
                <>
                  <AuthFill />
                  <DropDown
                    name="type_of_enquiry"
                    label="Type of Enquiry"
                    options={TYPE_OF_ENQUIRIES_ROADSIDE}
                    onChange={(e) => {
                      methods.setValue(
                        'enquiry_description',
                        TYPE_OF_ENQUIRIES_ROADSIDE[e.target.selectedIndex - 1]
                          .label,
                      )
                      methods.setValue(
                        'type_of_enquiry',
                        TYPE_OF_ENQUIRIES_ROADSIDE[e.target.selectedIndex - 1]
                          .value,
                      )
                    }}
                  />
                  <Title />
                  <FirstName />
                  <LastName />
                  <Email />
                  <MembershipNo optional />
                  <VehicleRegNo optional />
                  <Comments
                    optional
                    name="additional_info"
                    label="Your Question"
                  />
                  <MarketingTerms />
                  <Button submitResponse={submitResponse}>
                    {content.forms['roadside-assistance'].details.submit}
                  </Button>
                </>
              )}
              {/* MULTIMEDIA FORM */}
              {methods.getValues('form_name') === 'multimedia' && (
                <>
                  <AuthFill />
                  {brand === 'toyota' && (
                    <DropDown
                      name="type_of_enquiry"
                      label="Type of Enquiry"
                      options={TYPE_OF_ENQUIRIES_MULTIMEDIA}
                      onChange={(e) => {
                        methods.setValue(
                          'enquiry_description',
                          TYPE_OF_ENQUIRIES_MULTIMEDIA[
                            e.target.selectedIndex - 1
                          ].label,
                        )
                        methods.setValue(
                          'type_of_enquiry',
                          TYPE_OF_ENQUIRIES_MULTIMEDIA[
                            e.target.selectedIndex - 1
                          ].value,
                        )
                      }}
                    />
                  )}
                  <Title />
                  <FirstName />
                  <LastName />
                  <Email />
                  {brand === 'lexus' && <Tel optional />}
                  <VehicleRegNo />
                  <Comments
                    optional
                    className="resize-y w-full"
                    name="additional_info"
                    label="Your Question"
                    characterLimit={2000}
                  />
                  <MarketingTerms />
                  <Button submitResponse={submitResponse}>
                    {content.forms.multimedia.details.submit}
                  </Button>
                </>
              )}
              {/* FINANCE FORM */}
              {methods.getValues('form_name') === 'financial-services' && (
                <>
                  <AuthFill />
                  <DropDown
                    name="type_of_enquiry"
                    label="Type of Enquiry"
                    options={TYPE_OF_ENQUIRIES_FINANCE}
                    onChange={(e) => {
                      methods.setValue(
                        'enquiry_description',
                        TYPE_OF_ENQUIRIES_FINANCE[e.target.selectedIndex - 1]
                          .label,
                      )
                      methods.setValue(
                        'type_of_enquiry',
                        TYPE_OF_ENQUIRIES_FINANCE[e.target.selectedIndex - 1]
                          .value,
                      )
                    }}
                  />
                  <AgreementNo />
                  <Title />
                  <FirstName />
                  <LastName />
                  <Email />
                  <DateOfBirth />
                  <Tel />
                  <AddressLookup methods={methods} />
                  <Comments
                    optional
                    name="additional_info"
                    label="Additional Information"
                  />
                  <FinancialTerms />
                  <Button submitResponse={submitResponse}>
                    {content.forms['financial-services'].details.submit}
                  </Button>
                </>
              )}
              {/* BUSINESS FORM */}
              {methods.getValues('form_name') === 'fleet-services' && (
                <>
                  <AuthFill />
                  <Title />
                  <FirstName />
                  <LastName />
                  <CompanyName />
                  <Email />
                  <Tel />
                  <AddressLookup methods={methods} />
                  <Comments
                    optional
                    name="additional_info"
                    label="Reason for contact"
                  />
                  <MarketingTerms />
                  <Button submitResponse={submitResponse}>
                    {content.forms['fleet-services'].details.submit}
                  </Button>
                </>
              )}
            </form>
          </FormStep>
        </FormProvider>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'national'
  const content = await import(
    `../../content/${brand}/forms/contact-us/${form}.md`
  ).catch(() => null)
  const settings = await import(
    `../../content/${brand}/settings/global.md`
  ).catch(() => null)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'contact',
        form: 'lead-contact',
        variant: '',
        initialStep: 'select-enquiry',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

ContactUs.whyDidYouRender = true

export default ContactUs
